import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { ScoreBadge, type GuessCell } from "./guess-grid";

const ORANGE_ACCENT = "#ff8a3d";

export interface UserScoreRowData {
    guesses: GuessCell[][];
    isSolved: boolean;
    isGuest: boolean;
    lastGuessAt?: string | null;
    nickname?: string;
    initials?: string;
    profilePhoto?: string | null;
}

interface UserScoreRowProps {
    data: UserScoreRowData;
    onSave: () => void;
}

/**
 * Highlighted "Your row" at the top of the scoreboard feed.
 * Shows the current user's score with orange accent treatment.
 * Renders differently for registered users vs guests.
 */
export default function UserScoreRow({ data, onSave }: UserScoreRowProps) {
    const [expanded, setExpanded] = useState(false);
    const [isAvatarPhotoFailed, setIsAvatarPhotoFailed] = useState<boolean>(false);
    const { isGuest, guesses, isSolved, nickname, initials, profilePhoto, lastGuessAt } = data;
    useEffect(() => {
        setIsAvatarPhotoFailed(false);
    }, [profilePhoto]);
    const bgColor = isGuest ? "rgba(255,138,61,0.08)" : "rgba(255,138,61,0.06)";
    const displayName = isGuest ? "You (unsaved)" : `${nickname || "You"} (You)`;
    const avatarInitials = initials || (nickname ? nickname[0].toUpperCase() : "Y");
    const timeLabel = useMemo(() => {
        if (!lastGuessAt) return "just now";
        const ts = moment(lastGuessAt);
        if (!ts.isValid()) return "just now";
        return ts.fromNow();
    }, [lastGuessAt]);

    return (
        <div
            className="tw-flex tw-gap-2.5 tw-py-3 tw-px-3.5"
            style={{ background: bgColor }}
        >
            {isGuest ? (
                <div
                    className="tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-rounded-full tw-font-bold"
                    style={{
                        width: 22,
                        height: 22,
                        background: "rgba(255,138,61,0.15)",
                        border: `1px solid ${ORANGE_ACCENT}`,
                        color: ORANGE_ACCENT,
                        fontSize: 12,
                    }}
                >
                    ?
                </div>
            ) : profilePhoto && !isAvatarPhotoFailed ? (
                <div
                    className="tw-relative tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-rounded-full tw-overflow-hidden"
                    style={{ width: 22, height: 22, background: ORANGE_ACCENT }}
                >
                    <img
                        src={profilePhoto}
                        alt="Your avatar"
                        className="tw-w-full tw-h-full tw-object-cover"
                        onError={() => {
                            setIsAvatarPhotoFailed(true);
                        }}
                    />
                </div>
            ) : (
                <div
                    className="tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-rounded-full tw-font-bold"
                    style={{
                        width: 22,
                        height: 22,
                        background: ORANGE_ACCENT,
                        color: "#fff",
                        fontSize: 10,
                    }}
                >
                    {avatarInitials}
                </div>
            )}

            <div className="tw-flex-1 tw-min-w-0">
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-0.5">
                    <span
                        className="tw-text-sm tw-font-semibold"
                        style={{ color: ORANGE_ACCENT }}
                    >
                        {displayName}
                    </span>
                    <span className="tw-text-xs tw-text-gray-500 tw-ml-auto">{timeLabel}</span>
                </div>

                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mt-1">
                    {isGuest ? (
                        <GuestScorePill
                            guesses={guesses}
                            isSolved={isSolved}
                            expanded={expanded}
                            onToggle={() => setExpanded((prev) => !prev)}
                        />
                    ) : (
                        <ScoreBadge
                            guesses={guesses}
                            expanded={expanded}
                            onToggle={() => setExpanded((prev) => !prev)}
                            isSolved={isSolved}
                        />
                    )}
                </div>

                {isGuest && (
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                        <span
                            className="tw-flex-1 tw-text-[11px]"
                            style={{ color: ORANGE_ACCENT }}
                        >
                            Save your score (expires at midnight)
                        </span>
                        <button
                            onClick={onSave}
                            className="tw-border-none tw-cursor-pointer tw-whitespace-nowrap"
                            style={{
                                background: ORANGE_ACCENT,
                                borderRadius: 12,
                                padding: "4px 12px",
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 500,
                            }}
                        >
                            Save it
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

const DOT_COLORS: Record<string, string> = {
    correct: "#7c3aed",
    close: "#f69929",
    wrong: "#4b5563",
    empty: "#fff",
};

function GuestScorePill({
    guesses,
    isSolved,
    expanded,
    onToggle,
}: {
    guesses: GuessCell[][];
    isSolved: boolean;
    expanded: boolean;
    onToggle: () => void;
}) {
    const scoreLabel = isSolved ? `${guesses.length} guess${guesses.length !== 1 ? "es" : ""}` : "X";

    return (
        <div className="tw-inline-flex tw-flex-col">
            <button
                onClick={onToggle}
                className="tw-inline-flex tw-items-center tw-cursor-pointer tw-gap-1.5 tw-rounded-lg tw-bg-transparent tw-py-1 tw-px-2"
                style={{
                    border: `1px dashed ${ORANGE_ACCENT}`,
                }}
            >
                <div
                    className="tw-flex tw-flex-col tw-gap-1"
                    style={{ filter: "blur(1.5px)", opacity: 0.7 }}
                >
                    {guesses.slice(0, 2).map((row, i) => (
                        <div key={i} className="tw-flex tw-gap-1">
                            {row.map((cell, j) => (
                                <div
                                    key={j}
                                    className="tw-rounded-sm"
                                    style={{
                                        width: 5.5,
                                        height: 5.5,
                                        background: DOT_COLORS[cell.status] || DOT_COLORS.wrong,
                                    }}
                                />
                            ))}
                        </div>
                    ))}
                </div>
                <span
                    className="tw-text-xs tw-font-semibold tw-whitespace-nowrap"
                    style={{ color: ORANGE_ACCENT }}
                >
                    {scoreLabel}
                </span>
            </button>

            {expanded && (
                <div
                    className="tw-mt-2 tw-inline-block tw-p-2.5 tw-rounded-lg"
                    style={{
                        background: "rgba(255,138,61,0.06)",
                        border: `1px dashed ${ORANGE_ACCENT}`,
                        filter: "blur(1.5px)",
                        opacity: 0.7,
                    }}
                >
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        {guesses.map((row, i) => (
                            <div key={i} className="tw-flex tw-gap-1">
                                {row.map((cell, j) => (
                                    <div
                                        key={j}
                                        className="tw-flex tw-items-center tw-justify-center tw-rounded tw-text-white tw-font-bold tw-w-6 tw-h-6 tw-text-sm"
                                        style={{
                                            background: DOT_COLORS[cell.status] || DOT_COLORS.wrong,
                                        }}
                                    >
                                        {cell.digit}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
